import React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Button, Box, useMediaQuery, Menu, MenuItem, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import useLogout from "../hooks/useLogout";
import { useUserProfile } from "../hooks/QueryHooks/useUserProfile";
import useAccountMenu, { renderMenuGroups, withoutPaths } from "../hooks/useAccountMenu";
import { features } from "../utils/features";
import { CustomLogoutIcon } from "./CustomIcons";
import CountBadge from "./CountBadge";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AddIcon from "@mui/icons-material/Add";
import { t } from "i18next";

type NavItem = {
  label: string;
  path: string;
  badge?: number;
  match?: (pathname: string) => boolean;
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // On an auth screen the original destination is already in the state; keep passing it on.
  const authState = ["/login", "/register", "/resetPassword"].includes(location.pathname)
    ? location.state
    : { returnTo: location.pathname + location.search + location.hash };
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("md"));
  const [user] = useAuthState(auth);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const { groups: accountGroups, savedCount, unreadCount } = useAccountMenu();
  // Loading the profile once per session also keeps the e-mail used for notifications current.
  useUserProfile(!!user);

  const handleMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);
  const handleMenuClose = React.useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleLogout = useLogout();

  const handleNavigationClick = React.useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const navItems: NavItem[] = React.useMemo(() => {
    const search: NavItem = {
      label: "Szukaj",
      path: "/",
      match: (p) => p === "/" || p.startsWith("/searchResult"),
    };
    // Sellers can use this without an account, so it stays in the nav for guests too.
    const findBuyers: NavItem = { label: "Znajdź kupujących", path: "/sell" };

    const myListings: NavItem = {
      label: "Moje ogłoszenia",
      path: "/my-listings",
      match: (p) => p.startsWith("/my-listings") || p === "/sell/new" || p.startsWith("/listing/"),
    };

    // Guests get to search through the logo; a lone "Szukaj" tab only adds noise.
    if (!user) {
      return [
        ...(features.marketplace ? [findBuyers] : []),
        ...(features.agencies ? [{ label: "Strony", path: "/agency-directory" }] : []),
        // Guests have no account menu on a computer, so this is their only way to the form.
        { label: "Zgłoś agencję", path: "/add-site" },
      ];
    }

    return [
      search,
      ...(features.marketplace ? [findBuyers, myListings] : []),
      {
        label: t("saved", { ns: "words", defaultValue: "Zapisane" }),
        path: "/subscriptions",
        badge: savedCount || undefined,
      },
      ...(features.messages
        ? [{
            label: t("messages", { ns: "words", defaultValue: "Wiadomości" }),
            path: "/conversations",
            badge: unreadCount || undefined,
            match: (p: string) => p === "/conversations" || p.startsWith("/conversations/"),
          }]
        : []),
    ];
  }, [user, savedCount, unreadCount]);

  const isActive = (item: NavItem) =>
    item.match ? item.match(location.pathname) : location.pathname === item.path;

  const renderNavLink = (item: NavItem) => {
    const active = isActive(item);
    return (
      <Box
        key={item.path}
        component={Link}
        to={item.path}
        aria-current={active ? "page" : undefined}
        sx={{
          px: 1.75,
          py: 1,
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          color: active ? "var(--ink)" : "var(--muted)",
          background: active ? "var(--line-soft)" : "transparent",
          borderRadius: "8px",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          userSelect: "none",
          textDecoration: "none",
          transition: "color .15s, background .15s",
          "&:hover": { color: "var(--ink)", background: "var(--line-soft)" },
        }}
      >
        {item.label}
        {item.badge ? <CountBadge count={item.badge} /> : null}
      </Box>
    );
  };

  // Anything already shown in the top nav would only repeat itself in the menu.
  const menuGroups = withoutPaths(accountGroups, navItems.map((item) => item.path));

  const handleMenuItemClick = (path: string) => {
    handleNavigationClick(path);
    handleMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="default"
      role="banner"
      aria-label="primary navigation"
      sx={{
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        background: "#ffffff",
        color: "var(--ink)",
        zIndex: theme.zIndex.appBar,
        borderBottom: "1px solid var(--line)",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: 0.5, sm: 2 },
          px: { xs: 2, sm: 3, md: 5 },
        }}
      >
        {/* Left: logo. Left and right share the leftover width equally, so the nav sits in the page centre. */}
        <Box display="flex" alignItems="center" sx={{ gap: 1, flex: "1 1 0" }}>
          <Link
            to="/"
            aria-current={location.pathname === "/" ? "page" : undefined}
            style={{ display: "flex", alignItems: "center", textDecoration: "none", gap: 8 }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                background: theme.palette.primary.main,
                display: "grid",
                placeItems: "center",
                color: "#fff",
              }}
            >
              <HomeOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: "-0.01em",
                color: "var(--ink)",
              }}
            >
              Jesteś w domu
            </Typography>
          </Link>
        </Box>

        {/* Center: top nav (md+ only) */}
        <Box
          component="nav"
          sx={{
            display: { xs: "none", md: "flex" },
            gap: 0.25,
            flex: "0 1 auto",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          {navItems.map(renderNavLink)}
        </Box>

        {/* Right: actions */}
        <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ gap: 1, flex: "1 1 0" }}>

          {/* Guests go through login and come back to the form (PrivateRoute keeps the destination). */}
          {features.marketplace && !isXs && location.pathname !== "/sell/new" && (
            <Button
              component={Link}
              to="/sell/new"
              variant={user ? "contained" : "text"}
              size="small"
              startIcon={<AddIcon className="no-hover-icon" sx={{ color: "inherit" }} />}
              sx={{ textTransform: "none", whiteSpace: "nowrap", minHeight: 40, boxShadow: "none" }}
            >
              Dodaj ogłoszenie
            </Button>
          )}
          {/* On a phone the full button does not fit next to the logo; the label goes to aria/tooltip. */}
          {features.marketplace && isXs && location.pathname !== "/sell/new" && (
            <IconButton
              component={Link}
              to="/sell/new"
              aria-label="Dodaj ogłoszenie"
              title="Dodaj ogłoszenie"
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": { bgcolor: theme.palette.primary.dark },
              }}
            >
              <AddIcon className="no-hover-icon" sx={{ color: "#fff" }} />
            </IconButton>
          )}

          {!user && (
            <Button
              component={Link}
              to="/login"
              state={authState}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", whiteSpace: "nowrap", minHeight: 40 }}
            >
              {t("login", { ns: "action", defaultValue: "Zaloguj się" })}
            </Button>
          )}

          {!user && !isXs && (
            <Button
              component={Link}
              to="/register"
              state={authState}
              variant="contained"
              size="small"
              sx={{ textTransform: "none", whiteSpace: "nowrap", minHeight: 40, boxShadow: "none" }}
            >
              {t("register", { ns: "action", defaultValue: "Załóż konto" })}
            </Button>
          )}

          {!isXs && user && (
            <>
              <IconButton
                aria-label="Menu konta"
                aria-haspopup="menu"
                aria-controls={menuOpen ? "header-more-menu" : undefined}
                aria-expanded={menuOpen ? "true" : undefined}
                onClick={handleMenuOpen}
                size="small"
                disableRipple
              >
                <AccountCircleOutlinedIcon sx={{ fontSize: 24 }} />
              </IconButton>
              <Menu
                id="header-more-menu"
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {renderMenuGroups(menuGroups, handleMenuItemClick)}
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    handleLogout();
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <CustomLogoutIcon />
                    <Box component="span" sx={{ flex: 1 }}>{t("logOut", { ns: "action" })}</Box>
                  </Box>
                </MenuItem>
              </Menu>
            </>
          )}

        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
